import { getStorageInstance } from './config';
import { ref, getDownloadURL } from 'firebase/storage';

// Export Storage using lazy initialization with Proxy
// This ensures Storage is initialized only when accessed, preventing chunking issues
let _storageInstance: ReturnType<typeof getStorageInstance> | null = null;

export const Storage = new Proxy({} as ReturnType<typeof getStorageInstance>, {
    get(_target, prop) {
        if (!_storageInstance) {
            _storageInstance = getStorageInstance();
        }
        return (_storageInstance as any)[prop];
    },
    has(_target, prop) {
        if (!_storageInstance) {
            _storageInstance = getStorageInstance();
        }
        return prop in (_storageInstance as any);
    },
    ownKeys(_target) {
        if (!_storageInstance) {
            _storageInstance = getStorageInstance();
        }
        return Object.keys(_storageInstance as any);
    },
    getOwnPropertyDescriptor(_target, prop) {
        if (!_storageInstance) {
            _storageInstance = getStorageInstance();
        }
        return Object.getOwnPropertyDescriptor(_storageInstance as any, prop);
    }
});

export async function fetchJsonFile(fileName: string) {
    try {
        const fileRef = ref(getStorageInstance(), fileName);
        const url = await getDownloadURL(fileRef);
        const response = await fetch(url);
        const jsonData = await response.json();
        return jsonData;
    } catch (error: any) {
        if (error.code === 'storage/object-not-found') {
            console.warn(`File not found: "${fileName}". Returning empty object.`);
        } else {
            console.error(`Failed to fetch JSON file "${fileName}":`, error);
        }
        return {};
    }
}


/**
 * Fetches the download URL of a file in Firebase Storage.
 * @param filePath - The path to the file in Firebase Storage.
 * @returns The download URL as a string.
 */
export async function getFirebaseFileUrl(filePath: string): Promise<string> {
    if (!filePath) {
        return '';
    }
    try {
        const fileRef = ref(getStorageInstance(), filePath);
        const url = await getDownloadURL(fileRef);
        return url;
    } catch (error: any) {
        if (error.code !== 'storage/object-not-found') {
            console.error(`Error getting file URL for path: ${filePath}`, error);
        }
        // For 'storage/object-not-found', we can fail silently.
        return '';
    }
}